import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { CatHeading } from "blocks-nextjs/blocks-react/components/Heading/Heading";
import { CatLoadingIndicator } from "blocks-nextjs/blocks-react/components/LoadingIndicator/LoadingIndicator";
import { CatSegmentedControl } from "blocks-nextjs/blocks-react/components/SegmentedControl/SegmentedControl";
import { CatSegmentedControlItem } from "blocks-nextjs/blocks-react/components/SegmentedControlItem/SegmentedControlItem";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useHistoricalDateRange } from "../../hooks/useHistoricalDateRange";
import EssHistoricalEmptyState from "../../pages/Projects/EssHistoricalEmptyState";
import { getMethanolAssetEvents } from "../../services/api/project-asset-details.service";
import { useMethanolStatisticsQuery } from "../../services/query/methanol-statistics.query";
import { useMethanolAssetEventsQuery } from "../../services/query/project-asset-details.query";
import TimelineEmptyState from "../DataError/TimelineEmptyState";
import DateRangeDropdown from "../DateRangeDropdown/DateRangeDropdown";
import EssChargeHistoryTotal from "../EssHistoricalInfo/EssChargeHistoryTotal";
import { ESS_STATUS_CONFIG } from "../EssHistoricalInfo/essStatusConfig";
import CustomDatePickerPopover from "../GenerateLink/CustomDatePickerPopover";
import MethanolBarChart from "../MethanolCharts/MethanolBarChart";
import MethanolReformerStatusHistory from "../MethanolReformerStatusHistory/MethanolReformerStatusHistory";
import MethanolReformerTimeline, { mapAssetEventToTimelineEvent } from "../MethanolReformerTimeline/MethanolReformerTimeline";
import UnknownMachineEnergyChart from "./UnknownMachineEnergyChart";
const UNKNOWN_MACHINE_STATUS_CONFIG = {
    charging: { color: "#1AC69E", label: "Charging" }
};
const UNKNOWN_MACHINE_DISPLAY_ORDER = ["charging"];
// Charging variants that collapse into the "Charging" bucket (same as legacy AssetPageDetail)
const CHARGING_VARIANTS = new Set(["charging", "ac_charging", "dc_charging"]);
// Applies the same grouping as the legacy Activity History pie chart:
//   • DC_Charging / AC_Charging / Charging → "Charging"
//   • Idle                                 → "Idle"
//   • Everything else (Working, Self Discharge, Trickle, Tracking, Transported …) → "Discharging"
//   • Not Reported = max(0, numDays × 24 − total recorded)
// All 4 buckets are always returned, showing 0 when empty.
const normalizeMachineStatusHistory = (statusHistory, numDays = 0) => {
    if (!statusHistory)
        return undefined;
    let charging = 0;
    let discharging = 0;
    let idle = 0;
    statusHistory.forEach((entry) => {
        const key = entry.assetStatus.toLowerCase().replace(/\s+/g, "");
        if (CHARGING_VARIANTS.has(key)) {
            charging += entry.duration;
        }
        else if (key === "idle") {
            idle += entry.duration;
        }
        else {
            discharging += entry.duration;
        }
    });
    const totalRecorded = charging + discharging + idle;
    const notReporting = Math.max(0, Math.round((numDays * 24 - totalRecorded) * 100) / 100);
    return [
        { assetStatus: "Charging", duration: charging, unitOfMeasure: "hours" },
        { assetStatus: "Discharging", duration: discharging, unitOfMeasure: "hours" },
        { assetStatus: "Idle", duration: idle, unitOfMeasure: "hours" },
        { assetStatus: "Not Reporting", duration: notReporting, unitOfMeasure: "hours" }
    ];
};
const TIMELINE_PAGE_SIZE = 10;
// Reveal 4 rows up front then page in 3 at a time, matching AssetTimeline (the shared
// ESS/Machine timeline) so every drawer's Timeline reads identically.
const TIMELINE_INITIAL_REVEAL = 4;
const TIMELINE_REVEAL_STEP = 3;
const MAX_CUSTOM_DATE_RANGE_DAYS = 30;
/**
 * Custom event mapper for the MACHINE productivity timeline.
 * - Normalises Charging/AC_Charging/DC_Charging → "Charging started at X%".
 * - All other activities → "Charging stopped at X%".
 */
const mapMachineEventToTimelineEvent = (event, index) => {
    const isCharging = CHARGING_VARIANTS.has((event.activity ?? "").toLowerCase());
    const isIdle = (event.activity ?? "").toLowerCase() === "idle";
    // Mirror the original AssetPageDetail three-branch logic:
    //   Charging/AC_Charging/DC_Charging → "Charging started at X%"
    //   Idle                             → "Idle"
    //   Everything else                  → "Charging stopped at X%"
    const state = isCharging
        ? event.startSoc !== null && event.startSoc !== undefined
            ? `Charging started at ${Math.round(event.startSoc)}%`
            : "Charging"
        : isIdle
            ? "Idle"
            : event.endSoc !== null && event.endSoc !== undefined
                ? `Charging stopped at ${Math.round(event.endSoc)}%`
                : (event.activity ?? "");
    return {
        eventId: `${event.assetId ?? "event"}-${index}-${event.activityStartTimeStamp ?? index}`,
        startTime: event.activityStartTimeStamp ?? "",
        state
    };
};
/** Returns a valid cursor string or undefined */
const parseNextCursor = (cursor) => cursor && cursor !== "null" && cursor.trim() !== "" ? cursor : undefined;
/** Formats a Date to MM/DD/YYYY (UTC) or empty string */
const formatDateForPicker = (date) => date
    ? `${String(date.getUTCMonth() + 1).padStart(2, "0")}/${String(date.getUTCDate()).padStart(2, "0")}/${date.getUTCFullYear()}`
    : "";
// Energy Use History always shows the last 30 days ending at toDateISO (business requirement),
// independent of the selected date-range filter. Exported so callers that need to render the
// chart themselves (e.g. to position it after their own trailing sections) can derive the same
// value instead of duplicating this date math.
export const getEnergyChartFromDate = (toDateISO, fromDateISO) => {
    if (!toDateISO)
        return fromDateISO;
    const datePart = toDateISO.split("T")[0];
    const [yStr, mStr, dStr] = datePart.split("-");
    const y = Number(yStr);
    const m = Number(mStr);
    const d = Number(dStr);
    if (!y || !m || !d)
        return fromDateISO;
    const from = new Date(Date.UTC(y, m - 1, d - 29));
    if (isNaN(from.getTime()))
        return fromDateISO;
    const tzMatch = toDateISO.match(/(Z|[+-]\d{2}:\d{2})$/);
    const tz = tzMatch?.[1] ?? "";
    const fy = from.getUTCFullYear();
    const fm = String(from.getUTCMonth() + 1).padStart(2, "0");
    const fd = String(from.getUTCDate()).padStart(2, "0");
    return `${fy}-${fm}-${fd}T00:00:00.000${tz}`;
};
const UnknownMachineHistoricalInfo = ({ alertsSection, assetDrawerActive, assetId, hideEnergyChart = false, onChargeEnergyDataChange, onDateRangeResolved, onDischargeHoursChange, onEnergyDataChange, onProductivityDataChange, onTimelineDischargeEnergySumChange, projectId, projectTimezone, showProductivitySections = false, statCards, timezoneOffset }) => {
    const { customFromDate, customToDate, fromDateISO, handleCustomDateApply, handleCustomDateCancel, handleDateRangeChange, selectedDateRange, showCustomDatePicker, toDateISO } = useHistoricalDateRange({
        assetDrawerActive,
        projectTimezone,
        timezoneOffset
    });
    useEffect(() => {
        if (!assetDrawerActive) {
            return;
        }
        onDateRangeResolved?.({
            fromDateISO,
            selectedDateRange,
            toDateISO
        });
    }, [assetDrawerActive, fromDateISO, onDateRangeResolved, selectedDateRange, toDateISO]);
    const canFetch = assetDrawerActive && !!assetId && !!projectId && !!fromDateISO && !!toDateISO;
    // Number of days in the selected range — used to calculate Not Reported hours
    const selectedRangeNumDays = useMemo(() => {
        if (!fromDateISO || !toDateISO)
            return 0;
        const fromDate = new Date(fromDateISO.split("T")[0] + "T00:00:00Z");
        const toDate = new Date(toDateISO.split("T")[0] + "T00:00:00Z");
        if (isNaN(fromDate.getTime()) || isNaN(toDate.getTime()))
            return 0;
        return Math.round((toDate.getTime() - fromDate.getTime()) / (1000 * 60 * 60 * 24)) + 1;
    }, [fromDateISO, toDateISO]);
    const energyChartFromDate = useMemo(() => getEnergyChartFromDate(toDateISO, fromDateISO), [toDateISO, fromDateISO]);
    // ── Timeline pagination state ──
    const [timelineEvents, setTimelineEvents] = useState([]);
    const [timelineNextCursor, setTimelineNextCursor] = useState(undefined);
    const [timelineCurrentSkip, setTimelineCurrentSkip] = useState(0);
    const [loadMoreTrigger, setLoadMoreTrigger] = useState(0);
    const lastProcessedTrigger = useRef(0);
    // How many of the buffered timelineEvents are actually revealed to the user right now.
    const [timelineVisibleCount, setTimelineVisibleCount] = useState(TIMELINE_INITIAL_REVEAL);
    // Accumulated dischargeEnergy sum from all timeline pages (grows with Show More)
    const [accumulatedDischargeEnergy, setAccumulatedDischargeEnergy] = useState(0);
    const { data: initialTimelineData, isError: isTimelineError, isPending: isTimelineLoading } = useMethanolAssetEventsQuery({
        assetId,
        enabled: canFetch,
        fromDate: fromDateISO,
        limit: TIMELINE_PAGE_SIZE,
        projectId,
        toDate: toDateISO
    });
    // Reset effect declared BEFORE initial-data effect so that when both fire on the
    // same render cycle (e.g. drawer opens with data already cached), the initial-data
    // effect runs last and its setTimelineEvents/setAccumulatedDischargeEnergy calls win.
    const queryClient = useQueryClient();
    useEffect(() => {
        if (!assetDrawerActive) {
            queryClient.removeQueries({ queryKey: ["methanolStatistics", "energy", projectId, assetId] });
            queryClient.removeQueries({ queryKey: ["methanolStatistics", "runningHours", projectId, assetId] });
            queryClient.removeQueries({ queryKey: ["methanolStatistics", "productivity", projectId, assetId] });
            queryClient.removeQueries({ queryKey: ["methanolAssetEvents", projectId, assetId] });
        }
        else {
            queryClient.resetQueries({ queryKey: ["methanolStatistics", "energy", projectId, assetId] });
            queryClient.resetQueries({ queryKey: ["methanolStatistics", "productivity", projectId, assetId] });
            queryClient.resetQueries({ queryKey: ["methanolAssetEvents", projectId, assetId] });
        }
        setTimelineEvents([]);
        setTimelineNextCursor(undefined);
        setTimelineCurrentSkip(0);
        setLoadMoreTrigger(0);
        setTimelineVisibleCount(TIMELINE_INITIAL_REVEAL);
        setAccumulatedDischargeEnergy(0);
        lastProcessedTrigger.current = 0;
    }, [assetDrawerActive, fromDateISO, toDateISO, queryClient, projectId, assetId]);
    // Sync initial page → local state (declared AFTER reset effect so it wins on first render)
    useEffect(() => {
        if (initialTimelineData?.assetChargeActivities) {
            const rawEvents = [...initialTimelineData.assetChargeActivities].sort((a, b) => new Date(b.activityStartTimeStamp ?? 0).getTime() -
                new Date(a.activityStartTimeStamp ?? 0).getTime());
            const allEvents = showProductivitySections
                ? rawEvents.map((e, i) => mapMachineEventToTimelineEvent(e, i))
                : rawEvents.map((e, i) => mapAssetEventToTimelineEvent(e, i));
            setTimelineEvents(allEvents);
            setTimelineNextCursor(parseNextCursor(initialTimelineData.responseMetadata?.nextCursor));
            setTimelineCurrentSkip(TIMELINE_PAGE_SIZE);
            // Reset accumulated discharge energy to this page's sum
            const initialDischargeSum = rawEvents.reduce((sum, e) => sum + (e.dischargeEnergy ?? 0), 0);
            setAccumulatedDischargeEnergy(initialDischargeSum);
        }
        else {
            // Data not yet loaded or cleared — reset sum
            setAccumulatedDischargeEnergy(0);
        }
    }, [initialTimelineData, showProductivitySections]);
    const { data: loadMoreData, isError: isTimelineLoadMoreError, isFetching: isTimelineLoadingMore } = useQuery({
        enabled: loadMoreTrigger > 0 && !!timelineNextCursor && !!assetId && !!projectId,
        queryFn: async () => {
            return getMethanolAssetEvents(projectId, assetId, fromDateISO, toDateISO, {
                cursor: timelineNextCursor,
                limit: String(TIMELINE_PAGE_SIZE),
                skip: String(timelineCurrentSkip)
            });
        },
        queryKey: ["methanolAssetEventsLoadMore", projectId, assetId, fromDateISO, toDateISO, loadMoreTrigger],
        refetchOnWindowFocus: false,
        retry: false
    });
    useEffect(() => {
        if (loadMoreTrigger === 0 || loadMoreTrigger === lastProcessedTrigger.current)
            return;
        if (!loadMoreData?.assetChargeActivities)
            return;
        const rawEvents = [...loadMoreData.assetChargeActivities].sort((a, b) => new Date(b.activityStartTimeStamp ?? 0).getTime() - new Date(a.activityStartTimeStamp ?? 0).getTime());
        // Compute base index and new events inside the functional updater so that
        // prev.length reflects the actual current state rather than a stale closure.
        setTimelineEvents((prev) => {
            const baseIndex = prev.length;
            const newEvents = showProductivitySections
                ? rawEvents.map((e, i) => mapMachineEventToTimelineEvent(e, baseIndex + i))
                : rawEvents.map((e, i) => mapAssetEventToTimelineEvent(e, baseIndex + i));
            return [...prev, ...newEvents];
        });
        // Add this page's dischargeEnergy to the running total
        const pageDischargeSum = rawEvents.reduce((sum, e) => sum + (e.dischargeEnergy ?? 0), 0);
        setAccumulatedDischargeEnergy((prev) => prev + pageDischargeSum);
        setTimelineNextCursor(parseNextCursor(loadMoreData.responseMetadata?.nextCursor));
        setTimelineCurrentSkip((prev) => prev + TIMELINE_PAGE_SIZE);
        // A freshly-fetched API page still only reveals one more step, same as revealing
        // from the local buffer below.
        setTimelineVisibleCount((prev) => prev + TIMELINE_REVEAL_STEP);
        lastProcessedTrigger.current = loadMoreTrigger;
    }, [loadMoreData, loadMoreTrigger, assetId, showProductivitySections]);
    const handleTimelineLoadMore = useCallback(() => {
        // Reveal more of what's already buffered before hitting the API for another page.
        if (showProductivitySections && timelineVisibleCount < timelineEvents.length) {
            setTimelineVisibleCount((prev) => prev + TIMELINE_REVEAL_STEP);
            return;
        }
        if (isTimelineLoadingMore)
            return;
        setLoadMoreTrigger((prev) => prev + 1);
    }, [isTimelineLoadingMore, showProductivitySections, timelineVisibleCount, timelineEvents.length]);
    useEffect(() => {
        onTimelineDischargeEnergySumChange?.(accumulatedDischargeEnergy, isTimelineLoading || isTimelineLoadingMore);
    }, [accumulatedDischargeEnergy, isTimelineLoading, isTimelineLoadingMore, onTimelineDischargeEnergySumChange]);
    const { data: statusHistoryData, isError: isStatusHistoryError, isPending: isStatusHistoryLoading } = useMethanolStatisticsQuery({
        assetId,
        enabled: canFetch,
        fromDate: fromDateISO,
        projectId,
        statType: "runningHours",
        toDate: toDateISO
    });
    // Discharging hours for the selected range — mirrors the Activity History Discharging bucket
    const dischargeHoursFromHistory = useMemo(() => {
        if (!showProductivitySections || !statusHistoryData?.statusHistory)
            return null;
        const normalized = normalizeMachineStatusHistory(statusHistoryData.statusHistory, selectedRangeNumDays);
        const entry = normalized?.find((e) => e.assetStatus === "Discharging");
        return entry ? entry.duration : 0;
    }, [statusHistoryData, selectedRangeNumDays, showProductivitySections]);
    // Machine buckets shaped for the ESS breakdown panel, keyed off ESS_STATUS_CONFIG so the
    // machine page inherits the battery page's colours, tooltips and row ordering verbatim.
    const machineStatusMetrics = useMemo(() => {
        const statusHistory = statusHistoryData?.statusHistory;
        // An empty (but successfully fetched) response means no data, not four zero buckets.
        if (!statusHistory?.length)
            return [];
        const normalized = normalizeMachineStatusHistory(statusHistory, selectedRangeNumDays);
        if (!normalized)
            return [];
        const durationByKey = new Map(normalized.map((entry) => [entry.assetStatus.toLowerCase().replace(/\s+/g, ""), entry.duration]));
        return Object.entries(ESS_STATUS_CONFIG).map(([key, config]) => ({
            color: config.color,
            infoTooltip: config.tooltip,
            key,
            label: config.label,
            value: durationByKey.get(key) ?? 0
        }));
    }, [statusHistoryData, selectedRangeNumDays]);
    useEffect(() => {
        if (!showProductivitySections)
            return;
        onDischargeHoursChange?.(dischargeHoursFromHistory, isStatusHistoryLoading);
    }, [dischargeHoursFromHistory, isStatusHistoryLoading, onDischargeHoursChange, showProductivitySections]);
    const { data: productivityData, isPending: isProductivityLoading } = useMethanolStatisticsQuery({
        assetId,
        enabled: showProductivitySections && canFetch,
        fromDate: fromDateISO,
        projectId,
        statType: "productivity",
        toDate: toDateISO
    });
    const totalTonnes = useMemo(() => {
        if (!productivityData?.statDetails?.length)
            return null;
        let sum = 0;
        let hasData = false;
        productivityData.statDetails.forEach((entry) => {
            const e = entry;
            const details = e.productivityResponseDto ?? e.productivityDetails;
            const val = details?.productivity?.value;
            if (typeof val === "number") {
                sum += val;
                hasData = true;
            }
        });
        return hasData ? Math.round(sum * 100) / 100 : null;
    }, [productivityData]);
    // kWh/tonne: total discharge energy ÷ total productivity over the period.
    // A productivity-weighted average is used instead of a simple mean so that
    // high-volume days contribute proportionally — matching the metric name.
    const kWhPerTonne = useMemo(() => {
        if (!productivityData?.statDetails?.length)
            return null;
        let totalDischarge = 0;
        let totalProductivity = 0;
        productivityData.statDetails.forEach((entry) => {
            const e = entry;
            const details = e.productivityResponseDto ?? e.productivityDetails;
            const discharge = details?.dischargeEnergy?.value;
            const productivity = details?.productivity?.value;
            if (typeof discharge === "number")
                totalDischarge += discharge;
            if (typeof productivity === "number")
                totalProductivity += productivity;
        });
        if (totalProductivity === 0)
            return null;
        return Math.round((totalDischarge / totalProductivity) * 100) / 100;
    }, [productivityData]);
    useEffect(() => {
        onProductivityDataChange?.(totalTonnes, isProductivityLoading);
    }, [totalTonnes, isProductivityLoading, onProductivityDataChange]);
    // Daily bar chart data derived from the productivity API response.
    // - Fills every day in the selected range (missing days → "N/A")
    // - Latest date shown first (reversed order)
    // - Date formatted as MM/DD
    const productivityChartData = useMemo(() => {
        if (!fromDateISO || !toDateISO || !productivityData?.statDetails?.length)
            return [];
        const startDateStr = fromDateISO.split("T")[0];
        const endDateStr = toDateISO.split("T")[0];
        // Build a lookup: yyyy-MM-dd → productivity value
        const lookup = new Map();
        (productivityData?.statDetails ?? []).forEach((entry) => {
            const e = entry;
            const details = e.productivityResponseDto ?? e.productivityDetails;
            const datePart = e.startTimestamp?.split("T")[0] ?? "";
            if (datePart && typeof details?.productivity?.value === "number") {
                lookup.set(datePart, details.productivity.value);
            }
        });
        const result = [];
        const current = new Date(startDateStr + "T00:00:00Z");
        const end = new Date(endDateStr + "T00:00:00Z");
        while (current <= end) {
            const key = current.toISOString().split("T")[0];
            const mm = String(current.getUTCMonth() + 1).padStart(2, "0");
            const dd = String(current.getUTCDate()).padStart(2, "0");
            const label = `${mm}/${dd}`;
            result.push({ date: label, tooltipDate: key, value: lookup.get(key) ?? "N/A" });
            current.setUTCDate(current.getUTCDate() + 1);
        }
        return result.reverse(); // latest date first
    }, [productivityData, fromDateISO, toDateISO]);
    const kwhPerTonneChartData = useMemo(() => {
        if (!fromDateISO || !toDateISO || !productivityData?.statDetails?.length)
            return [];
        const startDateStr = fromDateISO.split("T")[0];
        const endDateStr = toDateISO.split("T")[0];
        const lookup = new Map();
        (productivityData?.statDetails ?? []).forEach((entry) => {
            const e = entry;
            const details = e.productivityResponseDto ?? e.productivityDetails;
            const datePart = e.startTimestamp?.split("T")[0] ?? "";
            if (datePart && typeof details?.efficiency?.value === "number") {
                lookup.set(datePart, details.efficiency.value);
            }
        });
        const result = [];
        const current = new Date(startDateStr + "T00:00:00Z");
        const end = new Date(endDateStr + "T00:00:00Z");
        while (current <= end) {
            const key = current.toISOString().split("T")[0];
            const mm = String(current.getUTCMonth() + 1).padStart(2, "0");
            const dd = String(current.getUTCDate()).padStart(2, "0");
            const label = `${mm}/${dd}`;
            result.push({ date: label, tooltipDate: key, value: lookup.get(key) ?? "N/A" });
            current.setUTCDate(current.getUTCDate() + 1);
        }
        return result.reverse(); // latest date first
    }, [productivityData, fromDateISO, toDateISO]);
    const [productivityTab, setProductivityTab] = useState("tonnes");
    const productivityTabIndex = productivityTab === "tonnes" ? 0 : 1;
    const handleProductivityTabChange = (e) => {
        setProductivityTab(e.detail.activeIndex === 0 ? "tonnes" : "kWhPerTonne");
    };
    // On first data arrival the container transitions from "No data" to the chart,
    // so MethanolBarChart mounts before the flex layout has settled its width.
    // Increment this key after the first render cycle to force a remount — on the
    // second mount the container already has a measured width from the first pass.
    const [chartRemountKey, setChartRemountKey] = useState(0);
    const chartDataReady = productivityChartData.length > 0 || kwhPerTonneChartData.length > 0;
    const prevChartDataReady = useRef(false);
    useEffect(() => {
        if (chartDataReady && !prevChartDataReady.current) {
            prevChartDataReady.current = true;
            setChartRemountKey((k) => k + 1);
        }
        if (!chartDataReady) {
            prevChartDataReady.current = false;
        }
    }, [chartDataReady]);
    const timelineContent = showProductivitySections ? (isTimelineLoading ? (_jsx(MethanolReformerTimeline, { compact: true, isLoading: true })) : isTimelineError ? (_jsx(MethanolReformerTimeline, { compact: true, events: [], isError: true, timezone: projectTimezone, timezoneLabel: timezoneOffset })) : (_jsx(MethanolReformerTimeline, { compact: true, events: timelineEvents.slice(0, timelineVisibleCount), hasMore: timelineVisibleCount < timelineEvents.length || !!timelineNextCursor, isLoadMoreError: isTimelineLoadMoreError, isLoadingMore: isTimelineLoadingMore, onLoadMore: handleTimelineLoadMore, timezone: projectTimezone, timezoneLabel: timezoneOffset }))) : isTimelineLoading ? (_jsx("div", { className: "flex justify-center items-center h-full min-h-[28vh] w-full", children: _jsx(CatLoadingIndicator, {}) })) : isTimelineError ? (_jsx(MethanolReformerTimeline, { events: [], isError: true, legacyCardStyling: true, timezone: projectTimezone, timezoneLabel: timezoneOffset })) : timelineEvents.length === 0 ? (_jsx(TimelineEmptyState, {})) : (_jsx(MethanolReformerTimeline, { events: timelineEvents, hasMore: !!timelineNextCursor, isLoadMoreError: isTimelineLoadMoreError, isLoadingMore: isTimelineLoadingMore, legacyCardStyling: true, onLoadMore: timelineNextCursor ? handleTimelineLoadMore : undefined, timezone: projectTimezone, timezoneLabel: timezoneOffset }));
    const statusHistoryContent = showProductivitySections ? (_jsx(EssChargeHistoryTotal, { emptyContent: _jsx(EssHistoricalEmptyState, { description: "No activity history is available for this asset in the selected range.", title: "No Data Available" }), error: isStatusHistoryError ? "Failed to load status history." : undefined, loading: isStatusHistoryLoading, metrics: machineStatusMetrics, title: "Activity History" })) : (_jsx(MethanolReformerStatusHistory, { assetId: assetId, chartSize: 150, displayOrder: UNKNOWN_MACHINE_DISPLAY_ORDER, isError: isStatusHistoryError, isLoading: isStatusHistoryLoading, projectId: projectId, restrictToDisplayOrder: true, rightColumnHeader: "Activity", showZeroValues: false, statusConfig: UNKNOWN_MACHINE_STATUS_CONFIG, statusHistory: statusHistoryData?.statusHistory, title: "Charge History Total" }));
    return (_jsxs("div", { className: "flex w-full px-4 mt-6 relative", "data-test": "unknown-historical-info-section", children: [_jsxs("div", { className: `flex flex-col w-full ${showProductivitySections ? "space-y-4" : "space-y-6"} pb-4`, "data-test": "unknown-historical-info-content", children: [_jsxs("div", { className: "flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3", "data-test": "unknown-historical-info-header", children: [_jsx(CatHeading, { "data-test": "unknown-historical-info-title", variant: "title", children: "Historical Info" }), _jsxs("div", { className: `${showProductivitySections ? "relative " : ""}flex items-center gap-4`, "data-test": "unknown-date-range-dropdown-wrapper", children: [_jsx(DateRangeDropdown, { "data-test": "unknown-date-range-dropdown", onValueChange: handleDateRangeChange, selectedValue: selectedDateRange }), showProductivitySections && (_jsx(CustomDatePickerPopover, { "data-test": "unknown-custom-date-picker-popover", initialEndDate: formatDateForPicker(customToDate), initialStartDate: formatDateForPicker(customFromDate), maxRangeDays: MAX_CUSTOM_DATE_RANGE_DAYS, onApply: handleCustomDateApply, onCancel: handleCustomDateCancel, show: showCustomDatePicker, timezoneOffset: timezoneOffset }))] })] }), statCards && _jsx("div", { "data-test": "unknown-stat-cards-slot", children: statCards }), _jsxs("div", { className: showProductivitySections
                            ? "grid grid-cols-1 gap-4 lg:grid-cols-2 lg:items-stretch"
                            : "flex flex-col lg:flex-row items-stretch gap-4", "data-test": "unknown-timeline-and-history-container", children: [_jsx("div", { className: `${showProductivitySections ? "" : "w-full lg:w-1/2 "}flex flex-col h-full`, "data-test": "timeline-container", children: _jsx("div", { className: "flex-1 min-h-0", children: timelineContent }) }), _jsx("div", { className: `${showProductivitySections ? "" : "w-full lg:w-1/2 "}flex flex-col gap-4`, "data-test": "activity-history-container", children: _jsx("div", { className: "flex-1 min-h-0", children: statusHistoryContent }) })] }), alertsSection && _jsx("div", { "data-test": "unknown-alerts-slot", children: alertsSection }), showProductivitySections && (_jsx("div", { className: "rounded border border-[#CCCCCC] bg-white min-h-max p-4", "data-test": "productivity-container", children: _jsxs("div", { className: "flex flex-col gap-3", "data-test": "productivity-header", children: [_jsxs("ol", { className: "flex items-start justify-between", children: [_jsxs("li", { className: "mr-auto", children: [_jsx("div", { className: "inline-block font-bold mb-4 pb-2 border-b-[3px] border-[#FFCD11]", children: _jsx(CatHeading, { "data-test": "productivity-title", variant: "title", children: "Productivity" }) }), productivityTab === "tonnes" && !isProductivityLoading && (_jsxs("div", { className: "text-[#3F3F3F]", "data-test": "productivity-total-moved-label", children: ["Total Moved:", " ", _jsx("span", { className: "font-bold text-xl", children: totalTonnes !== null
                                                                ? `${new Intl.NumberFormat("en-US").format(totalTonnes)} tonnes`
                                                                : "No data" })] })), productivityTab === "kWhPerTonne" && !isProductivityLoading && (_jsxs("div", { className: "text-[#3F3F3F]", "data-test": "productivity-efficiency-label", children: ["Total Moved:", " ", _jsx("span", { className: "font-bold text-xl", children: kWhPerTonne !== null ? `${kWhPerTonne} kWh/tonnes` : "No data" })] }))] }), _jsx("li", { className: "flex-shrink-0", children: _jsxs(CatSegmentedControl, { activeIndex: productivityTabIndex, arialabel: "Productivity tabs", "data-test": "productivity-segmented-control", onBlSegmentItemChange: handleProductivityTabChange, children: [_jsx(CatSegmentedControlItem, { "data-test": "productivity-tab-tonnes", children: "tonnes" }), _jsx(CatSegmentedControlItem, { "data-test": "productivity-tab-kwh-per-tonne", children: "kWh / tonnes" })] }) })] }), isProductivityLoading ? (_jsx("div", { className: "flex justify-center items-center h-full min-h-[28vh] w-full", "data-test": "productivity-loading", children: _jsx(CatLoadingIndicator, {}) })) : (_jsx("div", { className: "w-full", "data-test": `productivity-${productivityTab}-view`, children: productivityTab === "tonnes" ? (productivityChartData.length > 0 ? (_jsx(MethanolBarChart, { barColor: "#FB5A00", data: productivityChartData, isEnergyBarChart: true, legendLabel: "tonnes", testId: "productivity-tonnes-chart", unit: "t", xAxisLabel: "", yAxisMax: (() => {
                                            const nums = productivityChartData
                                                .map((d) => Number(d.value))
                                                .filter((n) => !isNaN(n));
                                            const max = nums.length > 0 ? Math.max(...nums) : 0;
                                            return Math.ceil(max / 200) * 200 || 200;
                                        })(), yAxisStepSize: 200 }, chartRemountKey)) : (_jsx("div", { className: "flex justify-center items-center h-full min-h-[28vh] w-full", "data-test": "productivity-tonnes-value", children: _jsx(EssHistoricalEmptyState, { description: "No productivity data is available for this asset in the selected range.", title: "No Data Available" }) }))) : kwhPerTonneChartData.length > 0 ? (_jsx(MethanolBarChart, { barColor: "#FB5A00", data: kwhPerTonneChartData, isEnergyBarChart: true, legendLabel: "kWh/tonnes", testId: "productivity-kwh-per-tonne-chart", unit: "", xAxisLabel: "", yAxisMax: 2, yAxisStepSize: 0.5 }, chartRemountKey)) : (_jsx("div", { className: "flex justify-center items-center h-full min-h-[28vh] w-full", "data-test": "productivity-kwh-per-tonne-value", children: _jsx(EssHistoricalEmptyState, { description: "No productivity data is available for this asset in the selected range.", title: "No Data Available" }) })) }))] }) })), !hideEnergyChart && (_jsx(UnknownMachineEnergyChart, { assetId: assetId, enabled: canFetch, fromDate: energyChartFromDate, legacyStyling: !showProductivitySections, onChargeEnergyChange: onChargeEnergyDataChange, onTotalDischargeChange: onEnergyDataChange, projectId: projectId, showDischargeEnergy: false, timezone: projectTimezone || "UTC", toDate: toDateISO }))] }), !showProductivitySections && (_jsx("div", { className: "absolute top-12 right-0 z-50 w-full flex justify-end px-4", "data-test": "unknown-custom-date-picker-popover-wrapper", children: _jsx(CustomDatePickerPopover, { "data-test": "unknown-custom-date-picker-popover", initialEndDate: formatDateForPicker(customToDate), initialStartDate: formatDateForPicker(customFromDate), maxRangeDays: MAX_CUSTOM_DATE_RANGE_DAYS, onApply: handleCustomDateApply, onCancel: handleCustomDateCancel, show: showCustomDatePicker, timezoneOffset: timezoneOffset }) }))] }));
};
export default UnknownMachineHistoricalInfo;
